import React from 'react';
import { 
  ArrowLeft, AlertTriangle, CheckCircle2, Clock, ArrowUpRight,
  User, Calendar, ClipboardList, FileText, Image, Link
} from 'lucide-react';
import { nonconformanceRecords, inspectionRecords } from '../../data/qualityControlData';
import { products } from '../../data/inventoryData';

const NonconformanceDetail = ({ nonconformanceId, onBack, onViewInspection }) => {
  // Find nonconformance record
  const nonconformance = nonconformanceRecords.find(nc => nc.id === nonconformanceId);
  
  if (!nonconformance) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium mb-2">找不到不合格品記錄</h2>
        <p className="text-gray-500 mb-4">無法找到指定的不合格品記錄</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          返回
        </button>
      </div>
    );
  }
  
  // Get product details
  const product = products.find(p => p.id === nonconformance.product_id) || { name: '未知產品', sku: '' };
  
  // Get inspection details
  const inspection = inspectionRecords.find(i => i.id === nonconformance.inspection_id);
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 'open':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="h-3 w-3 mr-1" />
            待處理
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <ArrowUpRight className="h-3 w-3 mr-1" />
            處理中
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            已關閉
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            未知
          </span>
        );
    }
  };
  
  // Get severity badge
  const getSeverityBadge = (severity) => {
    switch(severity) {
      case 'minor':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            輕微
          </span>
        );
      case 'major':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            重大
          </span>
        );
      case 'critical':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            嚴重
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            未知
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">不合格品詳情</h1>
        </div>
        <div className="flex items-center space-x-3">
          {getSeverityBadge(nonconformance.severity)}
          {getStatusBadge(nonconformance.status)}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Nonconformance Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex items-center">
              <div className="p-2 rounded-md mr-3 bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <h2 className="text-lg font-medium">{nonconformance.id}</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">基本資訊</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <User className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">報告人員</div>
                        <div className="text-sm text-gray-600">{nonconformance.reported_by}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">報告日期</div>
                        <div className="text-sm text-gray-600">{nonconformance.date}</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Link className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">關聯檢驗</div>
                        <div className="text-sm">
                          {inspection ? (
                            <button 
                              onClick={() => onViewInspection(inspection.id)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {inspection.id}
                            </button>
                          ) : '無關聯檢驗'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">產品資訊</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium">產品名稱</div>
                      <div className="text-sm text-gray-600">{product.name}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">產品編號</div>
                      <div className="text-sm text-gray-600">{product.sku}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">不合格數量</div>
                      <div className="text-sm text-gray-600">{nonconformance.quantity}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">問題描述</h3>
                <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {nonconformance.description}
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">處理方式</h3>
                <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 flex items-center">
                  <span className="font-medium mr-2">
                    {nonconformance.disposition === 'return-to-supplier' && '退回供應商'}
                    {nonconformance.disposition === 'rework' && '返工'}
                    {nonconformance.disposition === 'scrap' && '報廢'}
                    {nonconformance.disposition === 'use-as-is' && '特採'}
                  </span>
                  {nonconformance.disposition_notes && (
                    <span className="text-gray-500">- {nonconformance.disposition_notes}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Root Cause Analysis */}
          {nonconformance.root_cause_analysis && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">根本原因分析</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">原因分類</h3>
                    <div className="text-sm text-gray-700">{nonconformance.root_cause_analysis.category}</div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">詳細分析</h3>
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                      {nonconformance.root_cause_analysis.description}
                    </div>
                  </div>
                  
                  {nonconformance.root_cause_analysis.contributing_factors && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">影響因素</h3>
                      <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                        {nonconformance.root_cause_analysis.contributing_factors.map((factor, index) => (
                          <li key={index}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Images */}
          {nonconformance.images && nonconformance.images.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">問題照片</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {nonconformance.images.map(image => (
                    <div key={image.id} className="rounded-lg overflow-hidden border border-gray-200">
                      <img 
                        src={image.url} 
                        alt={image.description} 
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-3 bg-gray-50">
                        <p className="text-sm text-gray-700">{image.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Corrective Actions */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">改善措施</h2>
            </div>
            {nonconformance.corrective_actions && nonconformance.corrective_actions.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {nonconformance.corrective_actions.map(action => (
                  <div key={action.id} className="p-4">
                    <h3 className="text-sm font-medium">{action.action}</h3>
                    <div className="mt-2 flex flex-wrap gap-y-2 gap-x-4 text-xs text-gray-500">
                      <span>負責單位: {action.responsible}</span>
                      <span>期限: {action.deadline}</span>
                      <span className={`
                        ${action.status === 'completed' ? 'text-green-600' : 
                          action.status === 'in-progress' ? 'text-blue-600' : 'text-gray-600'}
                      `}>
                        狀態: 
                        {action.status === 'completed' && '已完成'}
                        {action.status === 'in-progress' && '進行中'}
                        {action.status === 'pending' && '待處理'}
                      </span>
                    </div>
                    {action.notes && (
                      <div className="mt-2 text-xs text-gray-500">
                        備註: {action.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                尚未建立改善措施
              </div>
            )}
          </div>
          
          {/* Preventive Actions */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">預防措施</h2>
            </div>
            {nonconformance.preventive_actions && nonconformance.preventive_actions.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {nonconformance.preventive_actions.map(action => (
                  <div key={action.id} className="p-4">
                    <h3 className="text-sm font-medium">{action.action}</h3>
                    <div className="mt-2 flex flex-wrap gap-y-2 gap-x-4 text-xs text-gray-500">
                      <span>負責單位: {action.responsible}</span>
                      <span>期限: {action.deadline}</span>
                      <span className={`
                        ${action.status === 'completed' ? 'text-green-600' : 
                          action.status === 'in-progress' ? 'text-blue-600' : 'text-gray-600'}
                      `}>
                        狀態: 
                        {action.status === 'completed' && '已完成'}
                        {action.status === 'in-progress' && '進行中'}
                        {action.status === 'pending' && '待處理'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                尚未建立預防措施
              </div>
            )}
          </div>
          
          {/* Cost Impact */}
          {nonconformance.cost_impact && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">成本影響</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">材料成本</div>
                      <div className="text-lg font-medium">${nonconformance.cost_impact.material_cost.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">人工成本</div>
                      <div className="text-lg font-medium">${nonconformance.cost_impact.labor_cost.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">處理成本</div>
                      <div className="text-lg font-medium">${nonconformance.cost_impact.processing_cost.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">其他成本</div>
                      <div className="text-lg font-medium">${nonconformance.cost_impact.other_cost.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <div className="text-sm font-medium text-gray-500">總成本</div>
                    <div className="text-xl font-semibold text-red-600">
                      ${(nonconformance.cost_impact.material_cost + 
                         nonconformance.cost_impact.labor_cost + 
                         nonconformance.cost_impact.processing_cost + 
                         nonconformance.cost_impact.other_cost).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NonconformanceDetail;
